import { IUserSingpass } from '@/store/modules/auth'
import {
  CollectionsQuery,
  CollectionQuery,
  ProductQuery,
  ShopQuery,
  CartQuery,
} from '@/types/storefront.generated'
import {
  AttributeInput,
  CartInput,
  CartLineInput,
  CartLineUpdateInput,
} from '@/types/storefront.types'
import {
  createStorefrontApiClient,
  StorefrontApiClient,
} from '@shopify/storefront-api-client'

export type Shop = ShopQuery['shop'];
export type CollectionList = CollectionsQuery['collections']['edges'][number]['node'][];
export type Collection = NonNullable<CollectionQuery['collection']>;
export type CollectionProduct = Collection['products']['edges'][number]['node'];
export type Product = NonNullable<ProductQuery['product']>;
export type ProductOption = Product['options'][number]
export type ProductVariant = Product['variants']['edges'][number]['node']
export type Cart = NonNullable<CartQuery['cart']>;
export type CartLine = Cart['lines']['edges'][number]['node']

let client: StorefrontApiClient

export const initShopifyClient = (
  accessToken: string,
  shopifyDomain: string
): void => {
  client = createStorefrontApiClient({
    storeDomain: shopifyDomain,
    apiVersion: '2026-01',
    publicAccessToken: accessToken,
  })
}

export const getShopifyClient = (): StorefrontApiClient => client

export const getShopifyShop = async (): Promise<Shop | undefined> => {
  const shopQuery = `#graphql
    query shop {
      shop {
        name
        id
      }
    }
  `

  const { data } = await client.request(shopQuery)
  return data?.shop
}

export const getShopifyCollections = async ({ first = 20, productsFirst = 20 } = {}): Promise<CollectionList> => {
  const collectionsQuery = `#graphql
    query collections($first: Int, $productsFirst: Int) {
      collections(first: $first) {
        edges {
          node {
            id
            handle
            title
            products(first: $productsFirst, filters: [{ available: true }]) {
              edges {
                node {
                  id
                  tags
                }
              }
            }
          }
        }
      }
    }
  `

  const { data } = await client.request(collectionsQuery, {
    variables: { first, productsFirst },
  })
  return data?.collections.edges.map((edge) => edge.node) ?? []
}

export const getShopifyCollection = async (
  id: string,
  { productsFirst = 20 } = {}
): Promise<Collection | null> => {
  const collectionQuery = `#graphql
    query collection($id: ID!, $productsFirst: Int!) {
      collection(id: $id) {
        id
        handle
        title
        products(first: $productsFirst, filters: [{ available: true }]) {
          edges {
            node {
              id
              descriptionHtml
              title
              tags
              featuredImage {
                src: url(transform:  {
                   preferredContentType: WEBP,
                   maxWidth: 106
                })
              }
              priceRange {
                minVariantPrice {
                  amount
                }
                maxVariantPrice {
                  amount
                }
              }
            }
          }
        }
      }
    }
  `

  const { data } = await client.request(collectionQuery, {
    variables: { id, productsFirst },
  })
  return data?.collection ?? null
}

export const getShopifyProduct = async (
  id: string
): Promise<Product | null> => {
  const productQuery = `#graphql
    query product($id: ID!) {
      product(id: $id) {
        id
        descriptionHtml
        handle
        title
        tags
        priceRange {
          minVariantPrice {
            amount
          }
          maxVariantPrice {
            amount
          }
        }
        options {
          id
          name
          optionValues {
            id
            name
          }
        }
        images(first: 5) {
          edges {
            node {
              src: url(transform:  {
                 preferredContentType: WEBP
                 maxWidth: 1500
              })
            }
          }
        }
        variants(first: 20) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              sku
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  `

  const { data } = await client.request(productQuery, { variables: { id } })
  return data?.product ?? null
}

export const createShopifyCart = async (
  input: CartInput = {}
): Promise<Cart> => {
  const cartCreateMutation = `#graphql
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                attributes {
                  key
                  value
                }
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    product {
                      id
                    }
                    title
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount {
              amount
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
  `

  const { data } = await client.request(cartCreateMutation, {
    variables: {
      input,
    },
  })

  if (!data?.cartCreate?.cart) {
    throw new Error('Cannot create cart')
  }

  return data.cartCreate.cart
}

export const getShopifyCart = async (id: string): Promise<Cart | null> => {
  const cartQuery = `#graphql
    query cart($id: ID!) {
      cart(id: $id) {
        id,
        lines(first: 250) {
          edges {
            node {
              id
              attributes {
                key
                value
              }
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  price {
                    amount
                  }
                  product {
                    id
                  }
                  title
                }
              }
            }
          }
        }
        cost {
          subtotalAmount {
            amount
          }
        }
        attributes {
          key
          value
        }
      }
    }
  `

  const { data } = await client.request(cartQuery, { variables: { id } })
  return data?.cart ?? null
}

export const getShopifyCheckoutUrl = async (
  id: string,
  singpass?: IUserSingpass
): Promise<string> => {
  const query = `#graphql
    query checkoutUrl($id: ID!) {
      cart(id: $id) {
        checkoutUrl
      }
    }
  `

  const { data } = await client.request(query, { variables: { id } })

  if (!data?.cart?.checkoutUrl) {
    throw new Error('Cart checkoutUrl not found')
  }

  let checkoutUrl = data.cart.checkoutUrl
  if (singpass) {
    checkoutUrl += `&checkout[email]=${singpass.email}`
    checkoutUrl += `&checkout[billing_address][first_name]=${singpass.firstname}`
    checkoutUrl += `&checkout[billing_address][last_name]=${singpass.lastname}`
    checkoutUrl += `&checkout[billing_address][address1]=${singpass.address.block} ${singpass.address.street}`
    checkoutUrl += '&checkout[billing_address][country]=SG'
    checkoutUrl += `&checkout[billing_address][zip]=${singpass.address.postal}`
  }
  return checkoutUrl
}

export const addShopifyCartLines = async (
  cartId: string,
  lines: CartLineInput[]
): Promise<Cart> => {
  const cartLinesAddMutation = `#graphql
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                attributes {
                  key
                  value
                }
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    product {
                      id
                    }
                    title
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount {
              amount
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
  `

  const { data } = await client.request(cartLinesAddMutation, {
    variables: {
      cartId,
      lines,
    },
  })

  if (!data?.cartLinesAdd?.cart) {
    throw new Error('Cannot add cart lines')
  }

  return data.cartLinesAdd.cart
}

export const updateShopifyCartLines = async (
  cartId: string,
  lines: CartLineUpdateInput[]
): Promise<Cart> => {
  const cartLinesUpdateMutation = `#graphql
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                attributes {
                  key
                  value
                }
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    product {
                      id
                    }
                    title
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount {
              amount
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
  `

  const { data } = await client.request(cartLinesUpdateMutation, {
    variables: {
      cartId,
      lines,
    },
  })

  if (!data?.cartLinesUpdate?.cart) {
    throw new Error('Cannot update cart lines')
  }

  return data.cartLinesUpdate.cart
}

export const removeShopifyCartLines = async (
  cartId: string,
  lineIds: string[]
): Promise<Cart> => {
  const cartLinesRemoveMutation = `#graphql
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                attributes {
                  key
                  value
                }
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    product {
                      id
                    }
                    title
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount {
              amount
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
  `

  const { data } = await client.request(cartLinesRemoveMutation, {
    variables: {
      cartId,
      lineIds,
    },
  })

  if (!data?.cartLinesRemove?.cart) {
    throw new Error('Cannot remove cart lines')
  }

  return data.cartLinesRemove.cart
}

export const updateShopifyCartAttributes = async (
  cartId: string,
  attributes: AttributeInput[]
): Promise<Cart> => {
  const cartAttributesUpdateMutation = `#graphql
    mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
      cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                attributes {
                  key
                  value
                }
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    product {
                      id
                    }
                    title
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount {
              amount
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
  `

  const { data } = await client.request(cartAttributesUpdateMutation, {
    variables: {
      cartId,
      attributes,
    },
  })

  if (!data?.cartAttributesUpdate?.cart) {
    throw new Error('Cannot update cart attributes')
  }

  return data.cartAttributesUpdate.cart
}
